import { useEffect, useRef, useState } from 'react';

import { createRealtimeClientSecret, executeRealtimeToolCall, prepareRealtimeTurn } from '@/api/api';
import { getErrorDetails, getErrorSummary } from '@/utils/errorMessages';
import { Agent, ChatMessage, ChatModel, CustomGPT, Personalization, RealtimeAssistantContext, RealtimeToolInvocation, RealtimeVoiceSessionState, RealtimeVoiceSessionStatus, VoiceSpeedLevel } from '@/api/models';

const VOICE_SPEED_VALUES: Record<VoiceSpeedLevel, number> = {
  slow: 0.75,
  normal: 1.0,
  fast: 1.25,
};

type TurnCompletePayload = {
  userTranscript: string;
  assistantTranscript: string;
  assistantContext: RealtimeAssistantContext | null;
  model: string;
};

type UseRealtimeVoiceSessionArgs = {
  enabled: boolean;
  conversationId?: string;
  currentModel: ChatModel | null;
  currentCustomGPT: CustomGPT | null;
  currentAgent: Agent | null;
  selectedTool: string | null;
  messages: ChatMessage[];
  personalization: Personalization;
  timezone: string;
  onTurnComplete: (payload: TurnCompletePayload) => Promise<void>;
};

const INITIAL_STATE: RealtimeVoiceSessionState = {
  status: 'idle',
  localTranscript: '',
  assistantTranscript: '',
  activeToolCall: null,
  isMuted: false,
  error: null,
};

const extractTextFromEvent = (event: any): string => {
  if (typeof event?.transcript === 'string') return event.transcript;
  if (typeof event?.text === 'string') return event.text;
  if (typeof event?.delta === 'string') return event.delta;
  if (typeof event?.arguments === 'string') return event.arguments;
  return '';
};

export const useRealtimeVoiceSession = ({
  enabled,
  conversationId,
  currentModel,
  currentCustomGPT,
  currentAgent,
  selectedTool,
  messages,
  personalization,
  timezone,
  onTurnComplete,
}: UseRealtimeVoiceSessionArgs) => {
  const [state, setState] = useState<RealtimeVoiceSessionState>(INITIAL_STATE);
  const [voiceSpeed, setVoiceSpeedState] = useState<VoiceSpeedLevel>('normal');
  const voiceSpeedRef = useRef<VoiceSpeedLevel>('normal');
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const userTranscriptRef = useRef('');
  const assistantTranscriptRef = useRef('');
  const deploymentRef = useRef('gpt-realtime-2');
  const activeToolCallRef = useRef<RealtimeToolInvocation | null>(null);
  const currentTurnContextRef = useRef<RealtimeAssistantContext | null>(null);
  const responseRequestedRef = useRef(false);
  const turnGenerationRef = useRef(0);
  // `response.done` only means the model stopped generating; over WebRTC the
  // audio it already sent keeps playing out of the output buffer. These track
  // the buffer so we do not report "listening" while Ally is still audible.
  const audioPlaybackActiveRef = useRef(false);
  const responseCompleteRef = useRef(false);
  const audioBufferEventsSeenRef = useRef(false);
  const connectedRef = useRef(false);
  const conversationIdRef = useRef(conversationId || '');
  const currentCustomGPTRef = useRef(currentCustomGPT);
  const currentAgentRef = useRef(currentAgent);
  const selectedToolRef = useRef(selectedTool);
  const messagesRef = useRef(messages);
  const personalizationRef = useRef(personalization);
  const timezoneRef = useRef(timezone);
  const onTurnCompleteRef = useRef(onTurnComplete);

  const updateStatus = (status: RealtimeVoiceSessionStatus) => {
    setState((prev) => ({ ...prev, status }));
  };

  // Back to listening only once the model has finished AND its audio has
  // drained. If the transport never reports buffer state we fall back to
  // settling on `response.done` alone rather than getting stuck in "speaking".
  const settleAfterSpeaking = () => {
    if (!responseCompleteRef.current) return;
    if (audioBufferEventsSeenRef.current && audioPlaybackActiveRef.current) return;
    setState((prev) => (prev.status === 'speaking' ? { ...prev, status: 'listening' } : prev));
  };

  useEffect(() => {
    conversationIdRef.current = conversationId || '';
    currentCustomGPTRef.current = currentCustomGPT;
    currentAgentRef.current = currentAgent;
    selectedToolRef.current = selectedTool;
    messagesRef.current = messages;
    personalizationRef.current = personalization;
    timezoneRef.current = timezone;
    onTurnCompleteRef.current = onTurnComplete;
  }, [conversationId, currentCustomGPT, currentAgent, selectedTool, messages, personalization, timezone, onTurnComplete]);

  const requestAssistantResponse = async (responseInstructions?: string) => {
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open') return;

    if (responseInstructions) {
      channel.send(JSON.stringify({
        type: 'response.create',
        response: {
          instructions: responseInstructions,
        },
      }));
      return;
    }

    channel.send(JSON.stringify({ type: 'response.create' }));
  };

  const cleanupConnection = async (nextStatus: RealtimeVoiceSessionStatus = 'ended') => {
    connectedRef.current = false;

    const channel = dataChannelRef.current;
    if (channel) {
      try {
        channel.close();
      } catch {
        // no-op
      }
    }
    dataChannelRef.current = null;

    const peerConnection = peerConnectionRef.current;
    if (peerConnection) {
      try {
        peerConnection.getSenders().forEach((sender) => sender.track?.stop());
        peerConnection.close();
      } catch {
        // no-op
      }
    }
    peerConnectionRef.current = null;

    const stream = localStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    localStreamRef.current = null;

    const remoteAudio = remoteAudioRef.current;
    if (remoteAudio) {
      remoteAudio.pause();
      remoteAudio.srcObject = null;
    }
    remoteAudioRef.current = null;

    userTranscriptRef.current = '';
    assistantTranscriptRef.current = '';
    activeToolCallRef.current = null;
    currentTurnContextRef.current = null;
    responseRequestedRef.current = false;
    turnGenerationRef.current = 0;
    audioPlaybackActiveRef.current = false;
    responseCompleteRef.current = false;
    setState({
      status: nextStatus,
      localTranscript: '',
      assistantTranscript: '',
      activeToolCall: null,
      isMuted: false,
      error: nextStatus === 'error' ? state.error : null,
      error_details: nextStatus === 'error' ? state.error_details : undefined,
    });
  };

  const finalizeTurn = async () => {
    const userTranscript = userTranscriptRef.current.trim();
    const assistantTranscript = assistantTranscriptRef.current.trim();
    if (!userTranscript || !assistantTranscript) return;

    try {
      await onTurnCompleteRef.current({
        userTranscript,
        assistantTranscript,
        assistantContext: currentTurnContextRef.current,
        model: deploymentRef.current,
      });
      userTranscriptRef.current = '';
      assistantTranscriptRef.current = '';
      currentTurnContextRef.current = null;
      setState((prev) => ({ ...prev, error: null, error_details: undefined }));
    } catch (error) {
      const errorDetails = getErrorDetails(error);
      setState((prev) => ({
        ...prev,
        error: getErrorSummary(error, errorDetails),
        error_details: errorDetails,
        status: 'error',
      }));
    } finally {
      responseRequestedRef.current = false;
    }
  };

  const handleToolCall = async (event: any) => {
    const toolName = event?.name || '';
    const callId = event?.call_id || event?.callId || '';
    const rawArguments = event?.arguments || '{}';
    let parsedArguments: Record<string, any> = {};
    if (typeof rawArguments === 'string') {
      try {
        parsedArguments = JSON.parse(rawArguments || '{}');
      } catch {
        parsedArguments = {};
      }
    } else if (rawArguments && typeof rawArguments === 'object') {
      parsedArguments = rawArguments;
    }
    const nextToolCall: RealtimeToolInvocation = {
      call_id: callId,
      tool_name: toolName,
      arguments: parsedArguments,
      status: 'running',
      error: null,
    };
    activeToolCallRef.current = nextToolCall;
    setState((prev) => ({ ...prev, activeToolCall: nextToolCall }));

    const result = await executeRealtimeToolCall({
      tool_name: toolName,
      arguments: nextToolCall.arguments,
      timezone: timezoneRef.current,
    }).catch((error: unknown) => error);

    const channel = dataChannelRef.current;
    if (!channel) return;

    if (!result || result instanceof Error) {
      const errorDetails = getErrorDetails(result || 'Tool execution failed.');
      const errorSummary = getErrorSummary(result || 'Tool execution failed.', errorDetails);
      const failedToolCall = { ...nextToolCall, status: 'error' as const, error: errorSummary, error_details: errorDetails };
      activeToolCallRef.current = failedToolCall;
      setState((prev) => ({ ...prev, activeToolCall: failedToolCall, error: errorSummary, error_details: errorDetails }));
      channel.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify({ error: errorSummary }),
        },
      }));
      await requestAssistantResponse();
      return;
    }

    const completedToolCall = { ...nextToolCall, status: 'complete' as const };
    activeToolCallRef.current = completedToolCall;
    setState((prev) => ({ ...prev, activeToolCall: completedToolCall }));
    channel.send(JSON.stringify({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(result.result ?? result),
      },
    }));
    await requestAssistantResponse();
  };

  const handleRealtimeEvent = async (event: any) => {
    const eventType = event?.type || '';

    if (eventType === 'error') {
      const eventError = event?.error?.message || 'Realtime session error.';
      const errorDetails = getErrorDetails(eventError, {
        upstream_code: typeof event?.error?.code === 'string' ? event.error.code : undefined,
      });
      setState((prev) => ({ ...prev, error: getErrorSummary(eventError, errorDetails), error_details: errorDetails, status: 'error' }));
      return;
    }

    if (eventType === 'input_audio_buffer.speech_started') {
      turnGenerationRef.current += 1;
      responseRequestedRef.current = false;
      // Barge-in: whatever was queued for playback is discarded server-side.
      audioPlaybackActiveRef.current = false;
      responseCompleteRef.current = false;
      userTranscriptRef.current = '';
      assistantTranscriptRef.current = '';
      setState((prev) => ({
        ...prev,
        status: 'listening',
        localTranscript: '',
        assistantTranscript: '',
      }));
      return;
    }

    if (eventType === 'response.created' || eventType === 'response.output_audio.delta') {
      if (eventType === 'response.created') responseCompleteRef.current = false;
      updateStatus('speaking');
      return;
    }

    if (eventType === 'output_audio_buffer.started') {
      audioBufferEventsSeenRef.current = true;
      audioPlaybackActiveRef.current = true;
      updateStatus('speaking');
      return;
    }

    if (eventType === 'output_audio_buffer.stopped' || eventType === 'output_audio_buffer.cleared') {
      audioBufferEventsSeenRef.current = true;
      audioPlaybackActiveRef.current = false;
      settleAfterSpeaking();
      return;
    }

    if (eventType === 'conversation.item.input_audio_transcription.delta') {
      const delta = extractTextFromEvent(event);
      if (delta) {
        userTranscriptRef.current += delta;
        setState((prev) => ({ ...prev, localTranscript: userTranscriptRef.current }));
      }
      return;
    }

    if (eventType === 'conversation.item.input_audio_transcription.completed') {
      const transcript = extractTextFromEvent(event).trim();
      if (transcript) {
        userTranscriptRef.current = transcript;
        currentTurnContextRef.current = null;
        setState((prev) => ({ ...prev, localTranscript: transcript }));

        if (!responseRequestedRef.current) {
          responseRequestedRef.current = true;
          const generation = turnGenerationRef.current;
          const preparedTurn = await prepareRealtimeTurn({
            conversation_id: conversationIdRef.current,
            user_transcript: transcript,
            currentCustomGPT: currentCustomGPTRef.current,
            currentAgent: currentAgentRef.current,
            personalization: personalizationRef.current,
            selected_tool: selectedToolRef.current || '',
            recent_messages: messagesRef.current,
            timezone: timezoneRef.current,
          });
          if (generation !== turnGenerationRef.current) return;
          if (preparedTurn && 'error_code' in preparedTurn) {
            const errorDetails = getErrorDetails(preparedTurn.error, preparedTurn.error_details, preparedTurn.status_code);
            setState((prev) => ({
              ...prev,
              error: getErrorSummary(preparedTurn.error, { ...errorDetails, error_code: preparedTurn.error_code }),
              error_details: { ...errorDetails, error_code: preparedTurn.error_code, error_type: preparedTurn.error_type },
            }));
            return;
          }
          if (preparedTurn && 'error' in preparedTurn) {
            const errorDetails = getErrorDetails(preparedTurn.error, preparedTurn.error_details, preparedTurn.status_code);
            setState((prev) => ({
              ...prev,
              error: getErrorSummary(preparedTurn.error, errorDetails),
              error_details: errorDetails,
            }));
            return;
          }
          if (preparedTurn?.citations?.length) {
            currentTurnContextRef.current = { citations: preparedTurn.citations };
          }
          await requestAssistantResponse(preparedTurn?.response_instructions || '');
        }
      }
      return;
    }

    if (eventType === 'response.output_audio_transcript.delta' || eventType === 'response.output_text.delta') {
      const delta = extractTextFromEvent(event);
      if (delta) {
        assistantTranscriptRef.current += delta;
        setState((prev) => ({ ...prev, assistantTranscript: assistantTranscriptRef.current }));
      }
      return;
    }

    if (eventType === 'response.output_audio_transcript.done' || eventType === 'response.output_text.done') {
      const transcript = extractTextFromEvent(event).trim();
      if (transcript) {
        assistantTranscriptRef.current = transcript;
        setState((prev) => ({ ...prev, assistantTranscript: transcript }));
      }
      return;
    }

    if (eventType === 'response.function_call_arguments.done') {
      await handleToolCall(event);
      return;
    }

    if (eventType === 'response.done') {
      // Set before awaiting so a buffer-drained event arriving mid-save still
      // settles the status; whichever lands last does the transition.
      responseCompleteRef.current = true;
      await finalizeTurn();
      settleAfterSpeaking();
    }
  };

  const startSession = async () => {
    if (!enabled || connectedRef.current || state.status === 'connecting') return;

    try {
      setState((prev) => ({ ...prev, status: 'connecting', error: null, error_details: undefined, activeToolCall: null }));
      const bootstrap = await createRealtimeClientSecret({
        conversation_id: conversationIdRef.current,
        currentModel,
        currentCustomGPT: currentCustomGPTRef.current,
        currentAgent: currentAgentRef.current,
        personalization: personalizationRef.current,
        selected_tool: selectedToolRef.current || '',
        recent_messages: messagesRef.current,
        timezone,
        preferred_speed: VOICE_SPEED_VALUES[voiceSpeedRef.current],
      });
      if (!bootstrap?.client_secret?.value) throw new Error('Realtime session bootstrap failed.');

      deploymentRef.current = bootstrap.deployment;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      localStreamRef.current = stream;

      const remoteAudio = new Audio();
      remoteAudio.autoplay = true;
      remoteAudioRef.current = remoteAudio;

      const peerConnection = new RTCPeerConnection();
      peerConnectionRef.current = peerConnection;
      peerConnection.ontrack = (rtcEvent) => {
        const [remoteStream] = rtcEvent.streams;
        if (remoteAudioRef.current && remoteStream) {
          remoteAudioRef.current.srcObject = remoteStream;
          void remoteAudioRef.current.play().catch(() => undefined);
        }
      };
      peerConnection.onconnectionstatechange = () => {
        const connectionState = peerConnection.connectionState;
        if (connectionState === 'failed' || connectionState === 'disconnected' || connectionState === 'closed') {
          const error = 'Realtime connection lost.';
          const errorDetails = getErrorDetails(error);
          setState((prev) => ({
            ...prev,
            status: connectionState === 'closed' ? 'ended' : 'error',
            error: connectionState === 'closed' ? prev.error : getErrorSummary(error, errorDetails),
            error_details: connectionState === 'closed' ? prev.error_details : errorDetails,
          }));
        }
      };

      stream.getTracks().forEach((track) => peerConnection.addTrack(track, stream));

      const dataChannel = peerConnection.createDataChannel('realtime-events');
      dataChannelRef.current = dataChannel;
      dataChannel.onopen = () => {
        connectedRef.current = true;
        dataChannel.send(JSON.stringify({ type: 'session.update', session: bootstrap.session }));
        updateStatus('listening');
      };
      dataChannel.onmessage = (messageEvent) => {
        try {
          const parsed = JSON.parse(messageEvent.data);
          void handleRealtimeEvent(parsed);
        } catch (error) {
          console.error('Failed to parse realtime event.', error);
        }
      };
      dataChannel.onerror = () => {
        const error = 'Realtime data channel error.';
        const errorDetails = getErrorDetails(error);
        setState((prev) => ({ ...prev, error: getErrorSummary(error, errorDetails), error_details: errorDetails, status: 'error' }));
      };

      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      const answerResponse = await fetch(bootstrap.webrtc_url, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${bootstrap.client_secret.value}`,
          'Content-Type': 'application/sdp',
        },
      });
      if (!answerResponse.ok) {
        throw new Error(`Realtime WebRTC negotiation failed (${answerResponse.status}).`);
      }
      const answerSdp = await answerResponse.text();
      await peerConnection.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    } catch (error) {
      await cleanupConnection('error');
      const errorDetails = getErrorDetails(error);
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: getErrorSummary(error, errorDetails),
        error_details: errorDetails,
      }));
    }
  };

  const stopSession = async (nextStatus: RealtimeVoiceSessionStatus = 'ended') => {
    await cleanupConnection(nextStatus);
  };

  const toggleMute = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextMuted = !state.isMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setState((prev) => ({ ...prev, isMuted: nextMuted }));
  };

  // Applies immediately to an active session via session.update; otherwise takes
  // effect on the next bootstrap request read from voiceSpeedRef.
  const setVoiceSpeed = (level: VoiceSpeedLevel) => {
    voiceSpeedRef.current = level;
    setVoiceSpeedState(level);
    const channel = dataChannelRef.current;
    if (channel && channel.readyState === 'open') {
      channel.send(JSON.stringify({
        type: 'session.update',
        session: { type: 'realtime', audio: { output: { speed: VOICE_SPEED_VALUES[level] } } },
      }));
    }
  };

  useEffect(() => {
    if (!enabled) {
      void stopSession('idle');
    }
  }, [enabled]);

  useEffect(() => {
    return () => {
      void cleanupConnection('idle');
    };
  }, []);

  return {
    state,
    startSession,
    stopSession,
    toggleMute,
    voiceSpeed,
    setVoiceSpeed,
    isActive: state.status === 'connecting' || state.status === 'listening' || state.status === 'speaking',
  };
};
